import { AdditionalYields, MulticallRetryFunction } from '../../types'
import { morphoIncludesUnlisted } from './morpho/unlisted'
import {
  Lender,
  isDolomite,
  isMidnight,
  isTerm,
  isExactly,
  isFlyingTulip,
  isLiquityFamily,
  isRiver,
  isTeller,
  isTermMax,
  isInverse,
  isCooler,
  isLlamaLend,
  isResupply,
  isCurvance,
  isTwyne,
  isFraxlend,
  isDssType,
  isFrankencoin,
  isSiloV2Type,
  isSiloV3Type,
} from '@1delta/lender-registry'
import { getLenderPublicData } from './fetchLender'
import { enrichMorphoCollateralTotals } from './morpho/collateralTotals'
import { getLenderPublicDataViaApi } from './fetchLenderExt'
import { SILO_API_SUPPORTED_CHAIN_IDS } from './silo-v2/fetchPublic'
import { hasDolomiteSubgraph } from '../dolomite-subgraph'
import { Chain } from '@1delta/chain-registry'
import { GenericTokenList } from '../../types/general'

// Toggle to silence lending-pipeline warnings. Set to `true` when debugging
// fetch/convert failures.
const LENDING_DEBUG = false
const warn = (...args: unknown[]) => {
  if (LENDING_DEBUG) console.warn(...args)
}

/**
 * Returns true when the lender should ONLY use the API path (no on-chain
 * fallback). Currently Morpho-type only — the Morpho GraphQL indexer has
 * been reliable on every chain it supports, so there's no benefit to
 * double-fetching.
 *
 * Exported so the config-consistency test can assert the invariant that makes
 * the Morpho list safe: a chain routed to the on-chain path MUST have a
 * `MORPHO_LENS` entry, otherwise `buildMorphoCall` produces a call with an
 * undefined address and the chain silently yields no markets at all.
 */
export function lenderApiOnly(lender: string, chainId: string) {
  if (lender === Lender.MORPHO_BLUE) {
    if (chainId === Chain.SONEIUM) return false
    if (chainId === Chain.HEMI_NETWORK) return false
    if (chainId === Chain.BERACHAIN) return false
    if (chainId === Chain.SEI_NETWORK) return false
    if (chainId === Chain.BNB_SMART_CHAIN_MAINNET) return false
    if (chainId === Chain.CELO_MAINNET) return false
    if (chainId === Chain.LISK) return false
    if (chainId === Chain.TAC_MAINNET) return false
    if (chainId === Chain.FLARE_MAINNET) return false
    if (chainId === Chain.PLUME_MAINNET) return false
    if (chainId === Chain.KAIA_MAINNET) return false
    // Pharos: Morpho markets live on-chain (in metadata) but blue-api /
    // Goldsky don't index chain 1672 — must use the on-chain path.
    if (chainId === Chain.PHAROS_MAINNET) return false
    if (chainId === '4114') return false
    // Same situation as Pharos: blue-api answers `unsupported chainId` for
    // both, and there is no Goldsky subgraph. Their markets are in the
    // registry and a MORPHO_LENS is now deployed on each, so the on-chain
    // path is the only one that can read them.
    if (chainId === Chain.GNOSIS) return false
    if (chainId === Chain.AVALANCHE_C_CHAIN) return false
    if (chainId === Chain.MEGAETH_MAINNET) return false
    if (chainId === Chain.XDC_NETWORK) return false
    // Lenses deployed 2026-08-03; blue-api indexes none of these either.
    if (chainId === Chain.SONIC_MAINNET) return false
    if (chainId === Chain.CRONOS_MAINNET) return false
    if (chainId === Chain.MORPH) return false
    if (chainId === Chain.MODE) return false
    if (chainId === Chain.LINEA) return false
    if (chainId === Chain.SCROLL) return false
    return true
  }
  // Midnight has no on-chain lens / pool path and no Goldsky/blue-api indexer —
  // its rates live in the off-chain order book (hosted Midnight API today). It
  // is API-only with no on-chain fallback (positions are fetched separately via
  // the user-data path).
  if (isMidnight(lender)) return true
  // Term Finance: public listings/rates come from the subgraph + static repo
  // config; no on-chain market fallback (user positions are fetched separately
  // via the on-chain user-data path). API-only, like Midnight.
  if (isTerm(lender)) return true
  // Exactly: routed through the same "external fetch" slot but the fetch itself
  // is a single ON-CHAIN Previewer eth_call (no API dependency, nothing to fall
  // back from). Lista's on-chain getMarketsFromChain uses the same trick.
  if (isExactly(lender)) return true
  // Flying Tulip: same trick as Exactly — the "external fetch" slot runs two
  // ON-CHAIN LendingLens multicall rounds (no API dependency, nothing to fall
  // back from).
  if (isFlyingTulip(lender)) return true
  // Liquity family: same trick as Exactly — the "external fetch" slot runs one
  // ON-CHAIN multicall per deployment (no API dependency; the api.liquity.org
  // stats endpoint is optional display enrichment, never the data source).
  if (isLiquityFamily(lender)) return true
  // River: same trick — the external-fetch slot runs one ON-CHAIN multicall
  // per chain (no API dependency at all).
  if (isRiver(lender)) return true
  // Teller: same trick — the external-fetch slot runs one ON-CHAIN multicall
  // over the curated pool list per chain (pool getters, no API dependency).
  if (isTeller(lender)) return true
  // Inverse FiRM: same trick — the external-fetch slot runs the hybrid
  // API + on-chain-verify pass (`fetchInverseMarkets`). Without this it routes
  // to the generic on-chain path, which has NO discovery branch for it and
  // returns an empty map, so no INVERSE market is ever created.
  if (isInverse(lender)) return true
  // Cooler (Olympus V2): same trick as Exactly — the external-fetch slot runs
  // one ON-CHAIN multicall over the single MonoCooler book (no API exists).
  // Without this it routes to the generic on-chain path, which has NO branch
  // for it and returns an empty map, so no COOLER market is ever created.
  if (isCooler(lender)) return true
  // TermMax: the external-fetch slot runs ONE `GET /market/data?chainId=` per
  // chain (markets are discovered, never a static registry). Same failure mode
  // as Inverse without this — the generic on-chain path cannot discover them.
  if (isTermMax(lender)) return true
  // Resupply: same trick — the external-fetch slot runs the registry discovery
  // + per-pair on-chain batch (`fetchResupplyMarkets`). Discovery is on-chain,
  // so without this the generic path returns an empty map and no RESUPPLY
  // market is ever created.
  if (isResupply(lender)) return true
  // Curvance: same trick — the external-fetch slot runs CentralRegistry
  // discovery plus the ProtocolReader's two calls. Discovery is on-chain, so
  // without this it routes to the generic on-chain path, which has NO discovery
  // branch for it and returns an empty map — no CURVANCE market would ever be
  // created.
  if (isCurvance(lender)) return true
  // Fraxlend: the external-fetch slot runs one ON-CHAIN batch over the curated
  // pair allowlist. There is no API, and the generic on-chain path has NO
  // discovery branch for it — without this it returns an empty map and no
  // FRAXLEND market is ever created.
  if (isFraxlend(lender)) return true
  // Twyne: the external-fetch slot runs one ON-CHAIN batch over the CURATED
  // market roster (the whitelists are un-enumerable on chain, so there is no
  // discovery to do here). Without this it routes to the generic on-chain path,
  // which has no branch for it and returns an empty map — no TWYNE market would
  // ever be created.
  if (isTwyne(lender)) return true
  // dss (Sky + the USDD fork): same trick as River — the external-fetch slot
  // runs one ON-CHAIN multicall per chain over the metadata ilk roster.
  // Without this it routes to the generic on-chain path, which has NO
  // discovery branch for it and returns an empty map.
  if (isDssType(lender)) return true
  // Frankencoin: the external-fetch slot runs one ON-CHAIN multicall over the
  // curated ORIGINAL-position roster per chain. Without this it routes to the
  // generic on-chain path, which has NO discovery branch for it and returns
  // an empty map, so no FRANKENCOIN market is ever created.
  if (isFrankencoin(lender)) return true
  // LlamaLend: the external-fetch slot runs the hybrid Curve-API + on-chain
  // pass (`fetchLlamaLendMarkets`). Same failure mode as Inverse without this
  // — the generic on-chain path has no discovery branch for it and returns an
  // empty map, so no LLAMALEND market is ever created.
  if (isLlamaLend(lender)) return true
  return false
}

/**
 * Returns true when the lender prefers the API path but allows an on-chain
 * fallback for markets the indexer didn't return. Used for Silo v2/v3 on
 * chains where the GraphQL indexer (`api-v3.silo.finance`) has data.
 */
function lenderApiWithOnChainFallback(lender: string, chainId: string) {
  if (isSiloV2Type(lender) || isSiloV3Type(lender)) {
    return SILO_API_SUPPORTED_CHAIN_IDS.has(chainId)
  }
  // Dolomite: prefer the subgraph (one query, rates included) where an endpoint
  // exists; fall back to the on-chain granular-getter path if the subgraph is
  // down or the chain has no subgraph.
  if (isDolomite(lender)) {
    return hasDolomiteSubgraph(chainId)
  }
  return false
}

export const getLenderPublicDataAll = async (
  chainId: string,
  lenders: string[],
  prices: { [asset: string]: number },
  additionalYields: AdditionalYields,
  multicallRetry: MulticallRetryFunction,
  tokenList?: () => Promise<GenericTokenList>,
  /**
   * Omit to take the per-chain default from `morphoIncludesUnlisted` — the
   * SAME resolver `fetchOraclePrices` uses, so the market roster and the price
   * roster cannot disagree. Pass a boolean only to override for one call.
   */
  includeUnlistedMorphoMarkets?: boolean,
): Promise<{ [lender: string]: any }> => {
  const includeUnlisted =
    includeUnlistedMorphoMarkets ?? morphoIncludesUnlisted(chainId)
  // API is the default. Every lender that supports an API goes through the
  // API path first; on-chain only runs for (a) lenders with no API support,
  // or (b) API-with-fallback lenders where the indexer didn't return data
  // for a specific market.
  const lendersApi = lenders.filter(
    (l) =>
      lenderApiOnly(l, chainId) || lenderApiWithOnChainFallback(l, chainId),
  )

  // 1. API fetch first — await before deciding which lenders still need RPC.
  let apiRes: { [lender: string]: any } = {}
  if (lendersApi.length > 0) {
    try {
      apiRes = await getLenderPublicDataViaApi(
        chainId,
        lendersApi,
        prices,
        additionalYields,
        tokenList,
        includeUnlisted,
      )
    } catch (e) {
      warn(
        `[lending] API fetch failed for chain ${chainId}:`,
        (e as any)?.message ?? e,
      )
    }
  }

  // 2. On-chain runs only for lenders where the API didn't deliver:
  //    - `lenderApiOnly` lenders (Morpho) NEVER fall back.
  //    - `lenderApiWithOnChainFallback` lenders (Silo v2/v3) fall back per
  //      market key: if `apiRes[lender]` is missing, hit the chain.
  //    - Lenders with no API support (Aave, Compound, Init, Euler, …) all
  //      go through the on-chain path as before.
  const lendersOnChain = lenders.filter((l) => {
    if (lenderApiOnly(l, chainId)) return false
    if (lenderApiWithOnChainFallback(l, chainId) && apiRes[l]) return false
    return true
  })

  let onChainRes: { [lender: string]: any } = {}
  if (lendersOnChain.length > 0) {
    try {
      onChainRes = await getLenderPublicData(
        chainId,
        lendersOnChain,
        prices,
        additionalYields,
        multicallRetry,
        tokenList,
      )
    } catch (e) {
      warn(
        `[lending] on-chain fetch failed for chain ${chainId}:`,
        (e as any)?.message ?? e,
      )
    }

    // Morpho Blue keeps no per-market collateral total, so the on-chain
    // converter cannot fill the collateral side from the lens payload alone.
    // One extra `balanceOf` round recovers it for every collateral token that
    // backs a single market — without it those rows publish `$0` TVL against a
    // live rate. See `morpho/collateralTotals.ts`.
    if (lendersOnChain.some((l) => l === Lender.MORPHO_BLUE)) {
      try {
        onChainRes = await enrichMorphoCollateralTotals(
          chainId,
          onChainRes,
          prices,
          multicallRetry,
        )
      } catch (e) {
        warn(
          `[lending] morpho collateral enrichment failed for chain ${chainId}:`,
          (e as any)?.message ?? e,
        )
      }
    }
  }

  // API results take precedence on any key collision — they're the source
  // of truth on the chains where the indexer is online.
  return { ...onChainRes, ...apiRes }
}
